import { Component, OnInit, Input, Output, EventEmitter} from '@angular/core';
import { MultiListBoxItem } from '../cpq-multi-list-box-with-selection/multi-list-box-item.model';
import { CpqMultiListBoxData } from './cpq-multi-list-box-with-recursion.model';
import { clone } from '../common/functions/common-helpers';
import { any, removeAllIfExist } from '../common/functions/js-linq';

@Component({
    selector: 'cpq-multi-list-box-with-recursion',
    templateUrl: './cpq-multi-list-box-with-recursion.component.html',
    styleUrls: ['./cpq-multi-list-box-with-recursion.component.scss'],
    standalone: false
})
export class CpqMultiListBoxWithRecursionComponent implements OnInit {
  public items : Array<MultiListBoxItem>;
  public searchQuery: string;
  public showOnlySelectedItems: boolean;
  public hasSearchResults = true;
  public displayNoResultsSearchMessage = false;
  public applyPermissions: boolean;
  @Input('selectedItems') selectedItems: Array<MultiListBoxItem>;
  @Input('data') data: CpqMultiListBoxData;
  @Input('hasSelectedChildrenTooltipText') hasSelectedChildrenTooltipText: string;
  @Output('applyPermissionsChanged') applyPermissionsChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output('selectedItemsChange') selectedItemsChange: EventEmitter<MultiListBoxItem[]> = new EventEmitter<MultiListBoxItem[]>();
  @Output('itemsLoaded') itemsLoaded: EventEmitter<MultiListBoxItem[]> = new EventEmitter<MultiListBoxItem[]>();

  constructor() { }

  ngOnInit() {
    this.applyPermissions = false;
    this.data.service.getItems().subscribe(items => {
      this.items = items;
      this.itemsLoaded.emit(items);
      this.setInitialStateForAllItems();
    });

  }

  public setInitialStateForAllItems() {
   this.collapseAllSubItems();

    if (this.data && this.data.selectedItems && this.data.selectedItems.length) {
      this.data.selectedItems.forEach(selectedItemId => {
        this.setSelectedItems(selectedItemId);
      });
    }
  }

  private collapseAllSubItems() {
    this.items.forEach(item => {
      item.isCollapsed = true;
      item.isVisible = true;
      item.isDisabled = !this.isItemSelectable(item);
      item.children.forEach(child => {
        this.setInitialState(child, item.id);
      });
    });
  }

  public selectAll() {
    this.items.forEach(item => this.selectItemWithAllSubitems(item));
    this.search();
    this.selectedItemsChange.emit(this.selectedItems);
  }

  private selectItemWithAllSubitems(item: MultiListBoxItem) {
    if (!item.isSelected && !item.isDisabled) {
      this.selectItem(item);
    }

    if (item.children) {
      item.children.forEach(child => this.selectItemWithAllSubitems(child));
    }
  }

  public search(item?: MultiListBoxItem) {
    this.setIsVisible(item);
    const temp = item ? item.children : this.items;
    temp.forEach(child => {
      this.search(child);
    });
  }

  private showItemWithFullPath(item: MultiListBoxItem) {
    item.isVisible = true;
    item.isCollapsed = false;
    if (item.parentId > 0) {
      let parentItem = this.getItem(this.items, i => i.id === item.parentId);
      this.showItemWithFullPath(parentItem);
    }
  }

  public setShowOnlySelectedState(checked: boolean) {
    this.showOnlySelectedItems = checked;
    if (this.showOnlySelectedItems) {
      this.items.filter(filteredItem => !this.selectedItems.some(selectedItem => selectedItem.id === filteredItem.id)).forEach(nonSelectedItem => nonSelectedItem.isVisible = false);
    }

    this.selectedItems.forEach(item =>
      this.showItemWithFullPath(item));

    this.search();
    this.displayNoResultsSearchMessage = false;
  }

  public clearCategorySearchInput() {
    this.searchQuery = "";
    this.search();
  }

  public expandCollapse(item: MultiListBoxItem) {
    item.isCollapsed = !item.isCollapsed;
    if (!item.isCollapsed) {
      if (this.showOnlySelectedItems && !item.hasSelectedChildren) {
        return;
      }

      item.children.forEach(child => {
        if (!this.showOnlySelectedItems || child.hasSelectedChildren || child.isSelected) {
          child.isVisible = true;
        }
      });
    } else {
      this.collapseItem(item);
    }
  }

  public select(item: MultiListBoxItem) {
    if (this.data.isMultiSelect) {
      this.selectItem(item);
    } else {
      const searchedItem = this.getItem(this.items, i => i.isSelected === true)
      if (searchedItem && searchedItem.id !== item.id) {
        searchedItem.isSelected = false;
        this.selectedItems.splice(0);
        this.setGroupParentCompletenessState(searchedItem);
      }

      item.isSelected = !item.isSelected;
      if (item.isSelected) {
        this.selectedItems.push(item);
      } else{
        this.selectedItems.splice(0);
      }

      this.setGroupParentCompletenessState(item);
      this.selectedItemsChange.emit(this.selectedItems);
    }
  }

  public deselect(item: MultiListBoxItem) {
    const itemIndex = this.selectedItems.findIndex(i => i.id === item.id);
    this.selectedItems.splice(itemIndex, 1);
    const searchedItem = this.getItem(this.items, i => i.id === item.id);
    searchedItem.isSelected = false;
    searchedItem.isVisible = true;
    if (!this.selectedItems.length) {
      this.setShowOnlySelectedState(false);
    }
    else if (this.showOnlySelectedItems) {
      this.hideUnselectedSubtree(searchedItem);
    }
  }

  public deselectAll() {
    removeAllIfExist(this.selectedItems, item => !item.isDisabled);
    if (this.showOnlySelectedItems && !this.selectedItems.length) {
      this.setShowOnlySelectedState(false);
    }

    this.items.forEach(item => {
      this.deselectItemWithAllSubitems(item);
      item.isCollapsed = !item.hasSelectedChildren;
      item.isVisible = !this.showOnlySelectedItems || item.hasSelectedChildren;
    });

    if (this.searchQuery) {
      this.search();
    }

    this.selectedItemsChange.emit(this.selectedItems);
  }

  private deselectItemWithAllSubitems(item: MultiListBoxItem): boolean {
    if (!item.isDisabled) {
      item.isSelected = false;
    }

    if (item.children) {
      let hasSelectedChildren = false;
      item.children.forEach(child => hasSelectedChildren = hasSelectedChildren || this.deselectItemWithAllSubitems(child));
      item.hasSelectedChildren = hasSelectedChildren;
    }

    const hasSelectedChildren = item.isSelected || item.hasSelectedChildren;
    item.isVisible = hasSelectedChildren;
    return hasSelectedChildren;
  }

  private collapseItem(item: MultiListBoxItem) {
    item.children.forEach(child => {
      child.isCollapsed = true;
      child.isVisible = false;
      this.collapseItem(child);
    });
  }

  private setInitialState(item: MultiListBoxItem, parentId: any) {
    item.isCollapsed = true;
    item.isVisible = false;
    item.parentId = parentId;
    item.isDisabled = !this.isItemSelectable(item);
    item.children.forEach(child => {
      this.setInitialState(child, item.id);
    });
  }

  private getItem(items: Array<MultiListBoxItem>, predicate: (value: MultiListBoxItem) => boolean): MultiListBoxItem {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (predicate(item)) {
        return item;
      } else {
        const searchedItem = this.getItem(item.children, predicate);
        if (searchedItem) {
          return searchedItem;
        }
      }
    }
  }

  private setSelectedItems(selectedItemId: any, item?: MultiListBoxItem) {
    if (item && item.id === selectedItemId) {
      this.selectItem(item);
      if (item.parentId) {
        item.isVisible = false;
      }
    }
    const tempItems = item ? item.children : this.items;
    tempItems.forEach(tempItem => {
      this.setSelectedItems(selectedItemId, tempItem);
    });
  }

  private selectItem(item: MultiListBoxItem) {
    if (!item.isSelected && !this.data.selectedItems.includes(item.id) && item.isDisabled) {
      return;
    }

    item.isSelected = !item.isSelected;
    if (item.isSelected) {
      const selectedItem = clone<MultiListBoxItem>(item);
      item.isVisible = true;
      this.selectedItems.push(selectedItem);

    } else {
      this.deselect(item);
    }

    this.setGroupParentCompletenessState(item);
    this.selectedItemsChange.emit(this.selectedItems);
  }

  private setGroupParentCompletenessState(item: MultiListBoxItem) {
    let groupParent = this.getGroupParent(item);
    if (item.id != groupParent.id) {
      groupParent.hasSelectedChildren = this.hasSelectedItem(groupParent.children);
      this.setGroupParentCompletenessState(groupParent);
    }
  }

  private getGroupParent(item: MultiListBoxItem) {
    let groupingParent = item;
    if (groupingParent.parentId) {
      groupingParent = this.getItem(this.items, i => i.id === groupingParent.parentId);
    }
    return groupingParent;
  }

  private hasSelectedItem(items: Array<MultiListBoxItem>): boolean {
    if (items) {
      return any(items, item => item.isSelected || this.hasSelectedItem(item.children));
    }

    return false;
  }


  private setIsVisible(item: MultiListBoxItem) {
    if (item) {
      if (this.searchQuery && item.name.toLocaleLowerCase().indexOf(this.searchQuery.toLocaleLowerCase()) === -1) {
        item.isVisible = false;
      } else {
        if ((this.showOnlySelectedItems && item.isSelected) || !this.showOnlySelectedItems) {
          this.setBranchVisible(item);
          this.hasSearchResults = true;
          this.displayNoResultsSearchMessage = false;
        } else {
          item.isVisible = false;
        }
      }
      return;
    }
      this.hasSearchResults = false;
      this.displayNoResultsSearchMessage = true;
  }

  private hideUnselectedSubtree(item: MultiListBoxItem) {
    if (!item || item.isSelected || this.hasSelectedItem(item.children)) {
      return;
    }

    item.isVisible = false;
    const parentItem = this.getItem(this.items, i => i.id === item.parentId);
    this.hideUnselectedSubtree(parentItem);
  }

  private setBranchVisible(item: MultiListBoxItem, isCollapsed: boolean = true) {
    item.isVisible = true;
    item.isCollapsed = isCollapsed;
    const parentItem = this.getGroupParent(item);
    if (parentItem !== item) {
      this.setBranchVisible(parentItem, false);
    }
  }

  private isItemSelectable(item: MultiListBoxItem) {
    if (!this.data.selectableItemIds) {
      return true;
    }

    return this.data.selectableItemIds.length && this.data.selectableItemIds.includes(item.id);
  }
}
